import React from 'react';
import { useController, FieldValues, UseControllerProps } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { InputTextArea } from 'components/_new/InputTextArea/InputTextArea';
import CustomLabel from './Label';

type ValidateTextAreaProps<T extends FieldValues> = UseControllerProps<T> & {
	containerClassName?: string;
	placeholder?: string;
	label?: string;
	labelClassName?: string;
	underLabel?: string;
	required?: boolean;
	rows?: number;
	links?: string[] | null;
	disabled?: boolean;
	onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
	maxLength?: number;
	autoComplete?: string;
	autoResize?: boolean;
};

const ValidateTextArea = <T extends FieldValues>({
	name,
	label,
	labelClassName,
	underLabel,
	placeholder,
	control,
	required = false,
	rules,
	rows,
	links,
	onChange,
	containerClassName,
	disabled = false,
	maxLength,
	autoComplete,
	autoResize,
}: ValidateTextAreaProps<T>) => {
	const { t } = useTranslation();

	const {
		field,
		fieldState: { error },
	} = useController({
		name,
		control,
		rules: {
			required: required
				? `${t('misc.forms.field')} ${label?.toLowerCase() ?? placeholder?.toLowerCase() ?? ''} ${t(
						'misc.forms.isRequired'
				  )}`
				: false,
			...rules,
		},
	});

	return (
		<div className={containerClassName}>
			{label && (
				<CustomLabel name={name} required={required} className={labelClassName}>
					<Trans
						components={
							links
								? links.map((link) => (
										// eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
										<a href={link} target="_blank" rel="noreferrer" className="p-link" />
								  ))
								: undefined
						}
					>
						{label}
					</Trans>
				</CustomLabel>
			)}
			{underLabel && <p className="mt-0 mb-2 px-3 text-sm text-muted">{underLabel}</p>}

			<InputTextArea
				{...field}
				onChange={(event) => {
					if (onChange) {
						onChange(event);
					}
					field.onChange(event);
				}}
				value={field.value}
				id={field.name}
				placeholder={placeholder}
				rows={rows ?? 4}
				disabled={disabled}
				maxLength={maxLength}
				autoComplete={autoComplete}
				autoResize={autoResize}
				className={classNames({
					'input-invalid': error,
				})}
			/>

			{error && <small className="p-error font-bold ml-3">{error.message}</small>}
		</div>
	);
};

export default ValidateTextArea;
